/*
* The MIT License (MIT)
*
* Copyright (c) 2016-2017 The Regents of the University of California
* Author: Arijit Panda
*
* Permission is hereby granted, free of charge, to any person obtaining a copy
* of this software and associated documentation files (the "Software"), to deal
* in the Software without restriction, including without limitation the rights
* to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
* copies of the Software, and to permit persons to whom the Software is
* furnished to do so, subject to the following conditions:
*
* The above copyright notice and this permission notice shall be included in
* all copies or substantial portions of the Software.
*
*
* THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
* IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
* FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
* AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
* LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
* OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
* THE SOFTWARE.
*/

import TrackBase from "../trackBase.js"
import paintAxis from "../util/paintAxis.js"
import MenuUtils from "../ui/menuUtils.js"
import HDF5IndexedReader from "./HDF5IndexedReader.js"
import {CNVpytorVCF} from "./cnvpytorVCF.js"
import FeatureSource from '../feature/featureSource.js'
import $ from "../vendor/jquery-3.3.1.slim.js"
import {createCheckbox} from "../igv-icons.js"
import IGVGraphics from "../igv-canvas.js"


/**
 * Represents 2 or more wig tracks overlaid on a common viewport.
 */

const DEFAULT_TRACK_HEIGHT = 250
const DEFAULT_BAF_TRACK_HEIGHT = 100

class CNVPytorTrack extends TrackBase {

    constructor(config, browser) {
        super(config, browser)
        this.featureType = 'numeric'
        this.paintAxis = paintAxis
        
        if (!config.max) {
            this.defaultScale = true
            this.autoscale = false
        }

        // Invoke height setter last to allocated to coverage and alignment tracks
        this.height = (config.height !== undefined ? config.height : DEFAULT_TRACK_HEIGHT)
    }

    async init(config) {

        this.type = "cnvpytor"
        this.graphType = config.graphType || "points"
        this.bin_size = config.bin_size || 100000
        this.signal_name = config.signal_name || "rd_snp"
        this.cnv_caller = config.cnv_caller || '2D'
        this.colors = config.colors || ['gray', 'black', 'green', 'blue']
        super.init(config)

    }

    get supportsWholeGenome() {
        return true
    }

    get_signals() {
        let signals = []

        if (this.signal_name == 'rd_snp') {
            signals = ["RD_Raw", "RD_Raw_gc_coor", this.cnv_caller, "BAF1", "BAF2"]

        } else if (this.signal_name == 'rd') {
            signals = ["RD_Raw", "RD_Raw_gc_coor", this.cnv_caller]

        } else if (this.signal_name == 'snp') {
            signals = ["BAF1", "BAF2"]

        } else if (this.signal_name == 'cnh') {
            signals = [this.cnv_caller]
        }
        return signals
    }

    get_signal_colors() {
        
        let signal_colors = [
            { singal_name: 'RD_Raw', color: this.colors[0] },
            { singal_name: 'RD_Raw_gc_coor', color: this.colors[1] },
            { singal_name: 'ReadDepth', color: this.colors[2] },
            { singal_name: '2D', color: this.colors[2] },
            { singal_name: 'BAF1', color: this.colors[3] },
            { singal_name: 'BAF2', color: this.colors[3] },
        ]
        return signal_colors
    }

    async postInit() {

        if (this.config.format == 'vcf') {
            this.featureSource = FeatureSource(this.config, this.browser.genome)
            this.header = await this.getHeader()


            var allVariants = this.featureSource.reader.features.reduce(function (r, a) {
                r[a.chr] = r[a.chr] || []
                r[a.chr].push(a)
                return r
            }, Object.create(null))

            const cnvpytor_obj = new CNVpytorVCF(allVariants, this.bin_size)
            //const wigFeatures = await cnvpytor_obj.computeReadDepth()
            //const bafFeatures = await cnvpytor_obj.computeBAF_v2()
            const dataWigs = await cnvpytor_obj.read_rd_baf()
            const wigFeatures = dataWigs[0]
            const bafFeatures = dataWigs[1]
            
            this.wigFeatures_obj = {}
            this.wigFeatures_obj[this.bin_size] = {
                "RD_Raw": wigFeatures[0],
                "RD_Raw_gc_coor": wigFeatures[1],
                "ReadDepth": wigFeatures[2],
                "2D": [],
                "BAF1": bafFeatures[0],
                "BAF2": bafFeatures[1]
            }
            this.rd_bins = [this.bin_size]
            this.available_callers = ["ReadDepth"]
            this.set_available_callers()

        } else {
            this.cnvpytor_obj =   new HDF5IndexedReader(this.config.url, this.bin_size)
            this.wigFeatures_obj = await this.cnvpytor_obj.get_rd_signal(this.bin_size)
            this.rd_bins = this.cnvpytor_obj.rd_bins
            this.available_callers = this.cnvpytor_obj.callers
            this.set_available_callers()
        }

        this.tracks = []
        const p = []

        this.signals = this.get_signals()
        this.signal_colors = this.get_signal_colors()

        for (let bin_size in this.wigFeatures_obj) {
            let i = 0

            for (const [signal_name, wig] of Object.entries(this.wigFeatures_obj[bin_size])) {

                if (this.signals.includes(signal_name)) {
                    let tconf = {}
                    tconf.type = "wig"
                    tconf.isMergedTrack = true
                    tconf.features = wig
                    tconf.name = signal_name
                    tconf.color = this.signal_colors.filter(x => x.singal_name === signal_name).map(x => x.color)
                    const t = await this.browser.createTrack(tconf)
                    if (t) {
                        t.autoscale = false     // Scaling done from merged track
                        this.tracks.push(t)
                    } else {
                        console.warn("Could not create track " + tconf)
                    }

                    if (typeof t.postInit === 'function') {
                        p.push(t.postInit())
                    }
                    i++
                }
            }

        }


        this.flipAxis = this.config.flipAxis ? this.config.flipAxis : false
        this.logScale = this.config.logScale ? this.config.logScale : false
        this.autoscale = this.config.autoscale
        if (!this.autoscale) {
            this.dataRange = {
                min: this.config.min || 0,
                max: this.config.max
            }
        }
        for (let t of this.tracks) {
            t.autoscale = false
            t.dataRange = this.dataRange
        }

        return Promise.all(p)
    }

    set_available_callers() {
        if (!this.available_callers.includes(this.cnv_caller)) {
            if (this.available_callers.length > 0) {
                this.cnv_caller = this.available_callers[0]
            } else {
                this.cnv_caller = null
            }
        }
    }

    async getHeader() {

        if (!this.header) {
            if (typeof this.featureSource.getHeader === "function") {
                const header = await this.featureSource.getHeader()
                if (header) {
                    this.callSets = header.callSets || []
                }
                this.header = header
            }
            this.sampleNames = this.callSets ? this.callSets.map(cs => cs.name) : []
        }

        return this.header
    }

    get height() {
        return this._height
    }

    set height(h) {
        this._height = h
        if (this.tracks) {
            for (let t of this.tracks) {
                t.height = h
                t.config.height = h
            }
        }
    }

    menuItemList() {
        let items = []

        if (this.flipAxis !== undefined) {
            items.push({
                label: "Flip y-axis",
                click: () => {
                    this.flipAxis = !this.flipAxis
                    this.trackView.repaintViews()
                }
            })
        }

        items = items.concat(MenuUtils.numericDataMenuItems(this.trackView))

        items.push('<hr/>')
        items.push("Bin Sizes")
        for (let rd_bin of this.rd_bins) {
            const checkBox = createCheckbox(rd_bin, rd_bin === this.bin_size)
            items.push({
                object: $(checkBox),
                click: async () => {
                    this.bin_size = rd_bin

                    await this.recreate_tracks(rd_bin)
                    this.clearCachedFeatures()
                    this.trackView.updateViews()
                    this.trackView.repaintViews()
                }
            })
        }
        items.push('<hr/>')
        items.push("Signal Type")

        let signal_dct = {"rd_snp": "RD and BAF Likelihood", "rd": "RD Signal", "snp": "BAF Likelihood"}
        for (let signal_name in signal_dct) {
            const checkBox = createCheckbox(signal_dct[signal_name], signal_name === this.signal_name)
            items.push({
                object: $(checkBox),
                click: async () => {
                    this.signal_name = signal_name
                    await this.recreate_tracks(this.bin_size)
                    this.clearCachedFeatures()
                    this.trackView.updateViews()
                    this.trackView.repaintViews()

                }
            })
        }

        // cnv caller setting
        items.push('<hr/>')
        items.push("CNV caller")
        for (let cnv_caller of this.available_callers) {
            const checkBox = createCheckbox(cnv_caller, cnv_caller === this.cnv_caller)
            items.push({
                object: $(checkBox),
                click: async () => {
                    this.cnv_caller = cnv_caller
                    await this.recreate_tracks(this.bin_size)
                    this.clearCachedFeatures()
                    this.trackView.updateViews()
                    this.trackView.repaintViews()
                }
            })
        }

        return items
    }

    async recreate_tracks(bin_size) {
        this.tracks = []
        const p = []

        if (!(bin_size in this.wigFeatures_obj)) {
            this.wigFeatures_obj = {...this.wigFeatures_obj, ...await this.cnvpytor_obj.get_rd_signal(bin_size)}
        }

        this.signals = this.get_signals()
        this.signal_colors = this.get_signal_colors()

        let i = 0

        for (const [signal_name, wig] of Object.entries(this.wigFeatures_obj[bin_size])) {
            if (this.signals.includes(signal_name)) {
                let tconf = {}
                tconf.type = "wig"
                tconf.isMergedTrack = true
                tconf.features = wig
                tconf.name = signal_name
                tconf.color = this.signal_colors.filter(x => x.singal_name === signal_name).map(x => x.color) 
                const t = await this.browser.createTrack(tconf)
                if (t) {
                    t.autoscale = false     // Scaling done from merged track
                    this.tracks.push(t)
                } else {
                    console.warn("Could not create track " + tconf)
                }

                if (typeof t.postInit === 'function') {
                    p.push(t.postInit())
                }
                i++
            }

        }


        this.flipAxis = this.config.flipAxis ? this.config.flipAxis : false
        this.logScale = this.config.logScale ? this.config.logScale : false
        this.autoscale = this.config.autoscale
        if (!this.autoscale) {
            this.dataRange = {
                min: this.config.min || 0,
                max: this.config.max
            }
        }
        for (let t of this.tracks) {
            t.autoscale = false
            t.dataRange = this.dataRange
        }
        return Promise.all(p)
    }

    async getFeatures(chr, bpStart, bpEnd, bpPerPixel) {

        if(this.tracks) {
            const promises = this.tracks.map((t) => t.getFeatures(chr, bpStart, bpEnd, bpPerPixel))
            return Promise.all(promises)
        } else {
            return undefined  // This can happen if a redraw is triggered before the track has initialized.
        }
    }

    // TODO: refactor to igvUtils.js
    getScaleFactor(min, max, height, logScale) {
        const scale = logScale ? height / (Math.log10(max + 1) - (min <= 0 ? 0 : Math.log10(min + 1))) : height / (max - min)
        return scale
    }

    computeYPixelValue(yValue, yScaleFactor) {
        return (this.flipAxis ? (yValue - this.dataRange.min) : (this.dataRange.max - yValue)) * yScaleFactor
    }

    computeYPixelValueInLogScale(yValue, yScaleFactor) {
        let maxValue = this.dataRange.max
        let minValue = this.dataRange.min
        if (maxValue <= 0) return 0 // TODO:
        if (minValue <= -1) minValue = 0
        minValue = (minValue <= 0) ? 0 : Math.log10(minValue + 1)
        maxValue = Math.log10(maxValue + 1)
        yValue = Math.log10(yValue + 1)
        return ((this.flipAxis ? (yValue - minValue) : (maxValue - yValue)) * yScaleFactor)
    }

    draw(options) {

        // const mergedFeatures = options.features    // Array of feature arrays, 1 for each track
        const mergedFeatures = options.features
        if(!mergedFeatures) return

        if (this.defaultScale) {
            if (this.signal_name == 'rd_snp') {
                this.dataRange = {
                    min: this.config.min || this.dataRange.min || -2,
                    max: this.config.max || this.dataRange.max || 6
                }
            } else if (this.signal_name == 'rd') {
                this.dataRange = {
                    min: this.config.min || this.dataRange.min || 0,
                    max: this.config.max || this.dataRange.max || 6
                }
            } else if (this.signal_name == 'snp') {
                this.dataRange = {
                    min: this.config.min || this.dataRange.min || -2,
                    max: this.config.max || this.dataRange.max || 0
                }
            }
        }

        if (this.autoscale) {
            this.dataRange = autoscale(options.referenceFrame.chr, mergedFeatures)
        }

        if(this.tracks) {
            for (let i = 0, len = this.tracks.length; i < len; i++) {
                const trackOptions = Object.assign({}, options)
                trackOptions.features = mergedFeatures[i]
                this.tracks[i].dataRange = this.dataRange
                this.tracks[i].flipAxis = this.flipAxis
                this.tracks[i].logScale = this.logScale
                if (this.graphType) {
                    this.tracks[i].graphType = this.graphType
                }
                this.tracks[i].draw(trackOptions)
            }
        }

        // guides lines
        const scaleFactor = this.getScaleFactor(this.dataRange.min, this.dataRange.max, options.pixelHeight, this.logScale)
        const yScale = (yValue) => this.logScale
            ? this.computeYPixelValueInLogScale(yValue, scaleFactor)
            : this.computeYPixelValue(yValue, scaleFactor)

         // Draw guidelines
         if (this.config.hasOwnProperty('guideLines')) {
            for (let line of this.config.guideLines) {
                if (line.hasOwnProperty('color') && line.hasOwnProperty('y') && line.hasOwnProperty('dotted')) {
                    let y = yScale(line.y)
                    let props = {
                        'strokeStyle': line['color'],
                        'strokeWidth': 1
                    }
                    if (line['dotted']) IGVGraphics.dashedLine(options.context, 0, y, options.pixelWidth, y, 5, props)
                    else IGVGraphics.strokeLine(options.context, 0, y, options.pixelWidth, y, props)
                }
            }
        }
    }

    popupData(clickState, features) {

        const featuresArray = features || clickState.viewport.cachedFeatures

        if (featuresArray && featuresArray.length === this.tracks.length) {
            // Array of feature arrays, 1 for each track
            const popupData = []
            for (let i = 0; i < this.tracks.length; i++) {
                if (i > 0) popupData.push('<hr/>')
                popupData.push(`<div style=background-color:rgb(245,245,245);border-bottom-style:dashed;border-bottom-width:1px;padding-bottom:5px;padding-top:10px;font-weight:bold;font-size:larger >${this.tracks[i].name}</div>`)
                const trackPopupData = this.tracks[i].popupData(clickState, featuresArray[i])
                popupData.push(...trackPopupData)

            }
            return popupData
        }
    }

}

function autoscale(chr, featureArrays) {

    let min = 0
    let max = -Number.MAX_VALUE
    for (let features of featureArrays) {
        for (let f of features) {
            if (typeof f.value !== 'undefined' && !Number.isNaN(f.value)) {
                min = Math.min(min, f.value)
                max = Math.max(max, f.value)
            }
        }
    }
    return {min: min, max: max}
}

export default CNVPytorTrack
